import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from "@/contexts/LanguageContext";
import { Mountains, InstagramLogo, FacebookLogo, WhatsappLogo, EnvelopeSimple, TiktokLogo, YoutubeLogo, GlobeHemisphereWest } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contact-info`).then(r => setInfo(r.data || {})).catch(() => {});
  }, []);

  const NAV = [
    { to: "/eventos", label: t("nav_events") }, { to: "/entrenamientos", label: t("nav_training") },
    { to: "/tienda", label: t("nav_store") }, { to: "/contacto", label: t("nav_contact") },
  ];

  // Build dynamic contact lines from CMS
  const contactLines = [];
  if (info) {
    const place = [info.address, info.city, info.region].filter(Boolean).join(", ");
    if (place) contactLines.push(place);
    if (info.email) contactLines.push(info.email);
    if (info.phone) contactLines.push(info.phone);
    if (info.schedule) contactLines.push(info.schedule);
  } else {
    contactLines.push("Coyhaique, Aysen, Chile");
  }

  // Helpers para construir URLs externas seguras
  const igHandle = (info?.instagram || "").replace(/^@/, "");
  const fbHandle = (info?.facebook || "").replace(/^@/, "");
  const ttHandle = (info?.tiktok || "").replace(/^@/, "");
  const ytHandle = (info?.youtube || "").replace(/^@/, "");
  const waNumber = (info?.whatsapp || "").replace(/\D/g, "");
  const websiteUrl = info?.website && !/^https?:\/\//i.test(info.website) ? `https://${info.website}` : info?.website;

  return (
    <footer className="bg-[#0a0a0a] border-t border-white/5 py-16" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4"><Mountains size={24} weight="bold" className="text-[#FF4D00]" /><span className="font-['Cabinet_Grotesk'] font-black text-lg uppercase tracking-tight">Pro Vertical</span></div>
            <p className="text-sm text-gray-400 leading-relaxed">{t("footer_desc")}</p>
          </div>
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-4">{t("navigation")}</h4>
            <div className="space-y-2">{NAV.map(l => <Link key={l.to} to={l.to} className="block text-sm text-gray-400 hover:text-[#FF4D00] transition-colors">{l.label}</Link>)}</div>
          </div>
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-4">{t("contact_title")}</h4>
            <div className="space-y-2 text-sm text-gray-400" data-testid="footer-contact-info">
              {contactLines.map((line, i) => <p key={i}>{line}</p>)}
              {info?.extra_info && <p className="text-xs text-gray-500 mt-2 leading-relaxed">{info.extra_info}</p>}
            </div>
          </div>
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-4">{t("social_media")}</h4>
            <div className="flex flex-wrap gap-4">
              {igHandle && <a href={`https://instagram.com/${igHandle}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FF4D00] transition-colors" data-testid="footer-instagram" aria-label="Instagram"><InstagramLogo size={22} /></a>}
              {fbHandle && <a href={`https://facebook.com/${fbHandle}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FF4D00] transition-colors" data-testid="footer-facebook" aria-label="Facebook"><FacebookLogo size={22} /></a>}
              {ttHandle && <a href={`https://tiktok.com/@${ttHandle}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FF4D00] transition-colors" data-testid="footer-tiktok" aria-label="TikTok"><TiktokLogo size={22} /></a>}
              {ytHandle && <a href={`https://youtube.com/@${ytHandle}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FF4D00] transition-colors" data-testid="footer-youtube" aria-label="YouTube"><YoutubeLogo size={22} /></a>}
              {waNumber && <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FF4D00] transition-colors" data-testid="footer-whatsapp" aria-label="WhatsApp"><WhatsappLogo size={22} /></a>}
              {info?.email && <a href={`mailto:${info.email}`} className="text-gray-400 hover:text-[#FF4D00] transition-colors" data-testid="footer-email" aria-label="Email"><EnvelopeSimple size={22} /></a>}
              {websiteUrl && <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FF4D00] transition-colors" data-testid="footer-website" aria-label="Sitio web"><GlobeHemisphereWest size={22} /></a>}
            </div>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-white/5 text-center text-xs text-gray-500">&copy; {new Date().getFullYear()} Pro Vertical. {t("all_rights")}</div>
      </div>
    </footer>
  );
}
